"use client";

/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { db } from "@kkeut/lib/firebase/client";
import { getDoc, doc } from "firebase/firestore";

const T = {
  naver: { ko: "네이버 예약", en: "Naver Booking", ja: "ネイバー予約", zh: "Naver 预约", th: "จองผ่าน Naver" },
  kakao: { ko: "카카오톡 문의", en: "Kakao Chat", ja: "カカオトーク", zh: "Kakao 咨询", th: "สอบถาม KakaoTalk" },
  about: { ko: "온라인 예약", en: "Online Reservation", ja: "オンライン予約", zh: "在线预约", th: "จองออนไลน์" },
};

const L = (d, locale) => d?.[locale] ?? d?.ko ?? "";
const labelWithKrBadge = (label, locale) => (locale === "ko" ? label : `${label} (KR)`);

const withHttps = (url) => {
  if (!url) return "";
  const s = String(url).trim();
  if (s.startsWith("/")) return s;
  return /^https?:\/\//.test(s) ? s : `https://${s}`;
};

function defaultLocaleHref(href = "/", locale = "ko") {
  if (!href) return "#";
  if (/^https?:\/\//.test(href)) return href;
  const path = href.startsWith("/") ? href : `/${href}`;
  return locale === "ko" ? path : `/${locale}${path}`;
}

function CalendarCheckIcon({ className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <line x1="8" y1="2.5" x2="8" y2="6" />
      <line x1="16" y1="2.5" x2="16" y2="6" />
      <path d="M9.2 16.2l1.9 1.9 4-4" />
    </svg>
  );
}

/**
 * 모바일 하단 고정 footer (네이버 / 카카오 / 온라인 예약 3개 셀).
 *
 * @param {object} props
 * @param {string} [props.branchId] - Firestore document key
 * @param {object} [props.initialInfo] - { naverLink, kakaoLink } 직접 주입
 * @param {string} [props.locale="ko"]
 * @param {function} [props.withLocaleHref] - 로캘 prefix 함수 (없으면 기본 동작)
 * @param {string} [props.reservePath="/reserve"]
 */
export function MobileFooter({
  branchId,
  initialInfo = null,
  locale = "ko",
  withLocaleHref = defaultLocaleHref,
  reservePath = "/reserve",
}) {
  const [info, setInfo] = useState(initialInfo);

  useEffect(() => {
    if (initialInfo) return;
    if (!branchId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, branchId, "default"));
        if (cancelled) return;
        const d = snap.exists() ? snap.data() : {};
        setInfo({ naverLink: d?.naverLink, kakaoLink: d?.kakaoLink });
      } catch (e) {
        if (!cancelled) console.error("MobileFooter fetch 실패:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [branchId, initialInfo]);

  if (!info) return null;

  const naver = withHttps(info.naverLink);
  const kakao = withHttps(info.kakaoLink);

  return (
    <footer className="fixed inset-x-0 bottom-0 z-50 lg:hidden select-none">
      <div className="bg-[#ffc0cb] border-t border-white/20">
        <div className="grid grid-cols-3 divide-x divide-white/35">
          <a
            href={naver || "#"}
            target="_blank"
            rel="noopener noreferrer"
            className={`grid h-[64px] grid-rows-[24px,auto] place-items-center content-center ${naver ? "text-black/90" : "pointer-events-none opacity-40"}`}
            aria-label={labelWithKrBadge(L(T.naver, locale), locale)}
          >
            <span className="inline-flex items-center justify-center h-[24px] w-[24px]">
              <Image
                src="/assets/naverCalendarLogo.png"
                alt=""
                width={22}
                height={22}
                className="block h-[22px] w-[22px]"
              />
            </span>
            <span className="text-[12px] leading-[1.1]">
              {labelWithKrBadge(L(T.naver, locale), locale)}
            </span>
          </a>

          <a
            href={kakao || "#"}
            target="_blank"
            rel="noopener noreferrer"
            className={`grid h-[64px] grid-rows-[24px,auto] place-items-center content-center ${kakao ? "text-black/90" : "pointer-events-none opacity-40"}`}
            aria-label={labelWithKrBadge(L(T.kakao, locale), locale)}
          >
            <span className="inline-flex items-center justify-center h-[24px] w-[24px]">
              <Image
                src="/assets/kakaoLogo.png"
                alt=""
                width={22}
                height={22}
                className="block h-[22px] w-[22px]"
              />
            </span>
            <span className="text-[12px] leading-[1.1]">
              {labelWithKrBadge(L(T.kakao, locale), locale)}
            </span>
          </a>

          <Link
            href={withLocaleHref(reservePath, locale)}
            className="grid h-[64px] grid-rows-[24px,auto] place-items-center content-center text-black/90"
            aria-label={L(T.about, locale)}
          >
            <span className="inline-flex items-center justify-center h-[24px] w-[24px]">
              <CalendarCheckIcon className="h-[22px] w-[22px]" />
            </span>
            <span className="text-[12px] leading-[1.1]">{L(T.about, locale)}</span>
          </Link>
        </div>
      </div>
      <div className="bg-[#ffc0cb]" style={{ height: "env(safe-area-inset-bottom)" }} />
    </footer>
  );
}

export default MobileFooter;
